import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import useCSSVariables from 'hooks/useCSSVariables';
import useCallbackRef from 'hooks/useCallbackRef';
import utils, { isEnterPressed } from 'utils';
import { TRANSITION_TIME } from 'data/global';

import './RadioSlider.styles.scss';

export type RadioSlide<T = string> = {
  label?: React.ReactNode | string;
  value?: T;
};

export type RadioSliderProps<T extends string> = {
  className?: string;
  name?: string;
  value?: T;
  defaultIndex?: number;
  slideSteps: Array<RadioSlide<T>>;
  sliding?: boolean;
  onChange?: (activeRadio: T) => void;
  customOnChange?: (activeRadio: T) => void;
};

type RadioOffsetMap = {
  leftOffset: number;
  width: number;
};

function RadioSlider<T extends string>(props: RadioSliderProps<T>) {
  const {
    className,
    name,
    value,
    slideSteps,
    defaultIndex = 0,
    sliding = true,
    onChange,
    customOnChange,
  } = props;

  const [offsetMap, setOffestMap] = useState<Array<RadioOffsetMap>>([]);

  const radioIndex = useMemo(
    () => slideSteps.findIndex((element) => element.value === value),
    [slideSteps, value],
  );

  const [activeIndex, setActiveIndex] = useState<number>(
    radioIndex !== -1 ? radioIndex : defaultIndex || 0,
  );

  const classes = classNames(['radio-slider', className]);

  const [rootContainer, rootContainerRef] = useCallbackRef<HTMLDivElement>();

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setOffestMap(
        (Array.from(rootContainer.children) as HTMLElement[]).map((item) => ({
          leftOffset: item.offsetLeft,
          width: item.clientWidth,
        })),
      );
    });

    setTimeout(() => {
      if (!rootContainer) return;
      resizeObserver.observe(rootContainer);
    }, 0);

    return () => {
      if (rootContainer) resizeObserver.unobserve(rootContainer);
    };
  }, [rootContainer]);

  const handleOnChange = useCallback(
    (value: T, index: number) => {
      setActiveIndex(index);
      onChange?.(value);
      customOnChange?.(value);
    },
    [customOnChange, onChange],
  );

  const scopedVariables = useCSSVariables({
    target: rootContainer,
    stylesObject: {
      width: `${offsetMap[activeIndex]?.width}px`,
      xOffset: `${offsetMap[activeIndex]?.leftOffset}px`,
      slidingTransition: sliding ? `${TRANSITION_TIME}ms` : '0',
    },
  });

  useEffect(
    () =>
      radioIndex !== -1
        ? setActiveIndex(radioIndex)
        : setActiveIndex(defaultIndex),
    [radioIndex, defaultIndex],
  );

  return (
    <div className={classes} ref={rootContainerRef}>
      {scopedVariables}
      {slideSteps.map((item, index) => (
        <label
          key={index}
          tabIndex={0}
          onKeyDown={(e) => {
            if (isEnterPressed(e)) {
              handleOnChange(item.value, index);
            }
          }}
          className={classNames('radio-slider__label', {
            'radio-slider__selected': activeIndex === index,
          })}
        >
          <input
            hidden
            type="radio"
            name={name}
            value={item.value}
            checked={activeIndex === index}
            onChange={() => handleOnChange(item.value, index)}
          />
          {utils.renderIcon(item.label)}
        </label>
      ))}
    </div>
  );
}

export default RadioSlider;
